import Button from '@mui/material/Button';

import LoadingView from '@webclient/components/pages/search/sections/results/real-time/components/voice-mode-chat/animated-avatar/loading-view';
import {
  StyledBox,
  StyledCenteredStack,
  StyledDrawer,
  StyledImage,
} from '@webclient/components/pages/search/sections/results/real-time/components/voice-mode-chat/animated-avatar/styles';
import List from '@webclient/components/search-forms/gamification-search/actions/bots-selector/sections/list';

import CONFIG from '@webclient/lib/const/bots';
import createPublicImageUrl, {
  retrieveImageWithFallback,
} from '@webclient/lib/createPublicImageUrl';
import getAvatar from '@webclient/lib/getAvatar';

import useAppDispatch from '@webclient/hooks/useAppDispatch';
import useAppSelector from '@webclient/hooks/useAppSelector';
import useToggleState from '@webclient/hooks/useToggleState';

import { voiceModChat } from '@webclient/state/slices/search-session';

import ChevDownRounded from '~icons/knowz-iconify/chev-down';

import { motion } from 'framer-motion';

const BOT_SIZE = 70;
const BOTS_AVATAR_FALLBACK =
  'https://asset.knowz.com/assets/images/not-found-characters-avatar-%5Boptimised%5D.webp';

type Props = {
  loadingView?: boolean;
};

export default function AnimatedAvatar({ loadingView = false }: Props) {
  const { state: isDrawerOpen, toggle: toggleDrawer } = useToggleState(false);
  const dispatch = useAppDispatch();
  const voiceChatModeIsBusy = useAppSelector(
    (state) => state.searchSession.voiceChatMode.state === 'responding',
  );
  const bot = useAppSelector((state) => state.searchSession.result.bot);

  if (loadingView) {
    return <LoadingView />;
  }

  return (
    <StyledBox>
      <StyledCenteredStack>
        <motion.div
          animate={{
            scale: voiceChatModeIsBusy ? [0.9, 1.1] : 1,
          }}
          transition={{
            ease: 'easeInOut',
            duration: 2,
            repeat: voiceChatModeIsBusy ? Infinity : 0,
            repeatType: 'reverse',
            bounceStiffness: 50,
          }}
        >
          {bot && (
            <StyledImage
              isActive={voiceChatModeIsBusy}
              width={BOT_SIZE}
              height={BOT_SIZE}
              src={
                bot.id === 0
                  ? CONFIG.DEFAULT_KNOWZ_AI_BOT.avatar
                  : retrieveImageWithFallback(
                      createPublicImageUrl(getAvatar(bot.image)),
                      BOTS_AVATAR_FALLBACK,
                    )
              }
              alt={bot ? bot.name : CONFIG.DEFAULT_KNOWZ_AI_BOT.name}
            />
          )}
        </motion.div>
        <Button
          variant="text"
          color="inherit"
          disableRipple
          disableTouchRipple
          onClick={toggleDrawer}
          endIcon={<ChevDownRounded />}
          sx={{
            backgroundColor: 'transparent!important',
          }}
        >
          {bot?.name}
        </Button>
        <StyledDrawer
          PaperProps={{
            sx: {
              paddingBlock: 6,
              paddingInline: 4,
            },
          }}
          anchor="right"
          open={isDrawerOpen}
          onClose={toggleDrawer}
        >
          <List
            onClose={toggleDrawer}
            onBotSwitch={(bot) => {
              dispatch(voiceModChat({ realtime: true, bot }));
            }}
          />
        </StyledDrawer>
      </StyledCenteredStack>
    </StyledBox>
  );
}
